import { useState } from "react";
import {
  Card,
  Table,
  Form,
  Row,
  Col,
  Badge,
  Button,
  Modal,
} from "react-bootstrap";
import { Link, useSearchParams } from "react-router-dom";
import { auditService } from "@/services/api";
import type { AuditEvent } from "@/services/api/audit-service";
import { useAsync } from "@/common/hooks/use-async";
import { AsyncBoundary } from "@/common/components/async-boundary";

const ACTION_VARIANT: Record<string, string> = {
  REPO_ONBOARDED: "success",
  REPO_ENABLED: "success",
  REPO_UPDATED: "primary",
  REPO_DISABLED: "warning",
  REPO_OFFBOARDED: "danger",
  EXCEPTION_APPROVED: "success",
  EXCEPTION_REJECTED: "danger",
  EXCEPTION_WITHDRAWN: "secondary",
  EXCEPTION_SUBMITTED: "info",
};

function fmt(v: unknown): string {
  if (v === null || v === undefined) return "∅";
  if (Array.isArray(v)) return v.length ? `[${v.join(", ")}]` : "[]";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

/** A field change is {from, to}; render as a readable before→after line. */
function isDiff(v: unknown): v is { from: unknown; to: unknown } {
  return typeof v === "object" && v !== null && ("from" in v || "to" in v);
}

/** The payload an event carries: a before→after diff, a flat detail, or nothing. */
function changePayload(e: AuditEvent): {
  kind: "diff" | "detail" | "none";
  count: number;
} {
  const diffKeys = Object.keys(e.changes ?? {});
  if (diffKeys.length) return { kind: "diff", count: diffKeys.length };
  const detailKeys = Object.keys(e.detail ?? {}).filter((k) => k !== "deleted");
  if (detailKeys.length) return { kind: "detail", count: detailKeys.length };
  return { kind: "none", count: 0 };
}

/** Compact, single-line cell: a count summary + a View button that opens the detail modal. */
function ChangesCell({ e }: { e: AuditEvent }) {
  const [show, setShow] = useState(false);
  const { kind, count } = changePayload(e);
  if (kind === "none") return <span className="text-muted">—</span>;
  const label =
    kind === "diff"
      ? `${count} field${count === 1 ? "" : "s"} changed`
      : `${count} field${count === 1 ? "" : "s"}`;
  return (
    <div
      className="d-flex align-items-center gap-2"
      style={{ whiteSpace: "nowrap" }}
    >
      <span className="text-muted small">{label}</span>
      <Button
        size="sm"
        variant="outline-secondary"
        className="py-0"
        onClick={() => setShow(true)}
      >
        View
      </Button>
      <ChangesModal e={e} kind={kind} show={show} onClose={() => setShow(false)} />
    </div>
  );
}

/** Modal detail of an audit event: a readable diff (config writes) + the raw JSON payload. */
function ChangesModal({
  e,
  kind,
  show,
  onClose,
}: {
  e: AuditEvent;
  kind: "diff" | "detail";
  show: boolean;
  onClose: () => void;
}) {
  const raw = kind === "diff" ? e.changes ?? {} : e.detail ?? {};
  const json = JSON.stringify(raw, null, 2);
  return (
    <Modal show={show} onHide={onClose} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">
          <Badge bg={ACTION_VARIANT[e.action] ?? "secondary"} className="me-2">
            {e.action}
          </Badge>
          <span className="text-muted">{e.resourceId}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="small text-muted mb-3">
          {e.timestamp?.replace("T", " ").replace(/\..*/, "")} UTC · by {e.actor}
        </div>
        {kind === "diff" && (
          <div
            className="mb-3"
            style={{
              fontFamily: "var(--bs-font-monospace)",
              fontSize: "0.85rem",
            }}
          >
            {Object.entries(e.changes ?? {}).map(([k, v]) => (
              <div key={k} className="mb-1">
                <span className="text-muted">{k}:</span>{" "}
                {isDiff(v) ? (
                  <>
                    <span className="text-danger">{fmt(v.from)}</span>
                    {" → "}
                    <span className="text-success">{fmt(v.to)}</span>
                  </>
                ) : (
                  <span>{fmt(v)}</span>
                )}
              </div>
            ))}
          </div>
        )}
        <details open={kind === "detail"}>
          <summary
            className="small text-muted mb-2"
            style={{ cursor: "pointer" }}
          >
            Raw JSON
          </summary>
          <pre
            className="bg-body-tertiary border rounded p-2 mb-0"
            style={{
              fontSize: "0.8rem",
              maxHeight: "50vh",
              overflow: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {json}
          </pre>
        </details>
      </Modal.Body>
      <Modal.Footer>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() => navigator.clipboard?.writeText(json)}
        >
          Copy JSON
        </Button>
        <Button size="sm" variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default function AuditList() {
  const [params] = useSearchParams();
  // Prefill from ?repo= (repo-detail "Activity →" link), but treat it as a contains filter.
  const [repoFilter, setRepoFilter] = useState(params.get("repo") ?? "");
  const [actionFilter, setActionFilter] = useState("");

  // Always fetch the global feed; filter the repo client-side (substring), like the PRs list.
  const { data, loading, error } = useAsync(
    () => auditService.listAudit({ limit: 500 }),
    [],
  );
  const all = data?.items ?? [];
  const needle = repoFilter.trim().toLowerCase();
  const items = all.filter(
    (e) =>
      (!needle || (e.resourceId ?? "").toLowerCase().includes(needle)) &&
      (!actionFilter || e.action === actionFilter),
  );
  const actions = Array.from(new Set(all.map((e) => e.action))).sort();

  return (
    <Card>
      <Card.Header>
        Activity
        {repoFilter ? (
          <>
            {" "}
            — matching <code>{repoFilter}</code>
          </>
        ) : (
          " (all repos)"
        )}
      </Card.Header>
      <Card.Body>
        <Row className="g-2 mb-3">
          <Col md={6}>
            <Form.Control
              placeholder="filter by repo — contains (e.g. ignite)"
              value={repoFilter}
              onChange={(e) => setRepoFilter(e.target.value)}
            />
          </Col>
          <Col md={3}>
            <Form.Select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
            >
              <option value="">All actions</option>
              {actions.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </Form.Select>
          </Col>
        </Row>
        <AsyncBoundary loading={loading} error={error}>
          <Table hover responsive size="sm">
            <thead>
              <tr>
                <th>When (UTC)</th>
                <th>Action</th>
                <th>Repo</th>
                <th>Actor</th>
                <th>Changes</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.auditId}>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {e.timestamp?.replace("T", " ").replace(/\..*/, "")}
                  </td>
                  <td>
                    <Badge bg={ACTION_VARIANT[e.action] ?? "secondary"}>
                      {e.action}
                    </Badge>
                  </td>
                  <td>
                    <Link
                      to={`/repos/detail?repo=${encodeURIComponent(e.resourceId)}`}
                    >
                      {e.resourceId}
                    </Link>
                  </td>
                  <td>{e.actor}</td>
                  <td>
                    <ChangesCell e={e} />
                  </td>
                </tr>
              ))}
              {!items.length && (
                <tr>
                  <td colSpan={5} className="text-muted">
                    No activity.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </AsyncBoundary>
      </Card.Body>
    </Card>
  );
}
